import React, { useEffect, useState } from "react";
import useSWR, { mutate } from "swr";
import { toast } from "sonner";
import { X, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inp = "w-full bg-[#221a12] border border-[#33281c] rounded-xl px-4 py-2.5 text-[#f6ecd4] outline-none focus:border-[#ff5147] focus:bg-[#2a2011] transition";
const SLOTS = ["10:00 AM", "12:30 PM", "1:00 PM", "3:00 PM", "3:30 PM", "5:30 PM", "6:00 PM"];
const fetcher = (url) => api.get(url).then((r) => r.data);

const priceFor = (p, hours) => (p ? p.base_price + Math.max(0, hours - p.base_hours) * p.additional_hour_price : 0);

function Field({ id, label, children }) {
  return <div><label htmlFor={id} className="text-xs text-[#8fa5a5]">{label}</label><div className="mt-1">{children}</div></div>;
}

export const NewReservationModal = ({ onClose }) => {
  const { data: content } = useSWR("/content", fetcher);
  const pricing = content?.pricing;
  const [f, setF] = useState({ name: "", email: "", phone: "", event_type: "Corporate Event", reservation_date: "", start_time: "", hours: 2, guest_count: "", notes: "", amount: "", send_email: true });
  const [priceTouched, setPriceTouched] = useState(false);
  const [taken, setTaken] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => { if (pricing && !priceTouched) set("amount", priceFor(pricing, Number(f.hours) || 0)); }, [pricing, f.hours, priceTouched]);
  useEffect(() => {
    if (!f.reservation_date) { setTaken([]); return; }
    api.get(`/availability?date=${f.reservation_date}`).then((r) => setTaken(r.data.taken || [])).catch(() => setTaken([]));
  }, [f.reservation_date]);

  const save = async () => {
    if (!f.name.trim() || !f.email.trim() || !f.reservation_date || !f.start_time) { toast.error("Please fill in name, email, date and start time."); return; }
    setSaving(true);
    try {
      await api.post("/admin/reservations", { ...f, hours: Number(f.hours), amount: Number(f.amount) || 0, guest_count: f.guest_count ? Number(f.guest_count) : null });
      toast.success(f.send_email ? "Reservation added and confirmation emailed." : "Reservation added.");
      mutate("/admin/reservations"); mutate("/admin/stats"); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add the reservation."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="new-res-title" className="card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-res-modal">
        <div className="flex justify-between items-center mb-1">
          <h3 id="new-res-title" className="font-display text-xl font-bold text-[#f6ecd4]">New Reservation</h3>
          <button onClick={onClose} aria-label="Close" data-testid="new-res-close"><X size={20} className="text-[#8fa5a5]" /></button>
        </div>
        <p className="text-xs text-[#8fa5a5] mb-4">Pay by check in person · paper agreement & waiver · website only (not sent to Bookeo).</p>
        <div className="space-y-3">
          <Field id="nr-name" label="Company / contact name"><input id="nr-name" className={inp} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="new-res-name" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field id="nr-email" label="Email"><input id="nr-email" type="email" className={inp} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="new-res-email" /></Field>
            <Field id="nr-phone" label="Phone"><input id="nr-phone" type="tel" className={inp} value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="new-res-phone" /></Field>
          </div>
          <Field id="nr-event" label="Event type"><input id="nr-event" className={inp} value={f.event_type} onChange={(e) => set("event_type", e.target.value)} data-testid="new-res-event" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field id="nr-date" label="Date"><input id="nr-date" type="date" className={inp} value={f.reservation_date} onChange={(e) => set("reservation_date", e.target.value)} data-testid="new-res-date" /></Field>
            <Field id="nr-time" label="Start time">
              <select id="nr-time" className={inp} value={f.start_time} onChange={(e) => set("start_time", e.target.value)} data-testid="new-res-time">
                <option value="">Choose…</option>
                {SLOTS.map((t) => <option key={t} value={t} disabled={taken.includes(t)}>{t}{taken.includes(t) ? " (booked)" : ""}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field id="nr-hours" label="Hours"><input id="nr-hours" type="number" min={1} max={24} className={inp} value={f.hours} onChange={(e) => set("hours", e.target.value)} data-testid="new-res-hours" /></Field>
            <Field id="nr-guests" label="Guests (max 50)"><input id="nr-guests" type="number" min={1} max={50} className={inp} value={f.guest_count} onChange={(e) => set("guest_count", e.target.value)} data-testid="new-res-guests" /></Field>
          </div>
          <Field id="nr-amount" label="Amount due by check ($) — filled from your pricing, change if needed">
            <input id="nr-amount" type="number" min={0} step="0.01" className={inp} value={f.amount} onChange={(e) => { setPriceTouched(true); set("amount", e.target.value); }} data-testid="new-res-amount" />
          </Field>
          <Field id="nr-notes" label="Notes"><textarea id="nr-notes" rows={2} className={inp} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="new-res-notes" /></Field>
          <label className="flex items-center gap-2 text-sm text-[#d9cbb0] cursor-pointer">
            <input type="checkbox" className="accent-[#ff5147] h-4 w-4" checked={f.send_email} onChange={(e) => set("send_email", e.target.checked)} data-testid="new-res-send-email" />
            Email a confirmation to the customer
          </label>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary w-full py-3 flex items-center justify-center gap-2 mt-5 disabled:opacity-60" data-testid="new-res-save"><Save size={16} /> {saving ? "Saving…" : "Add Reservation"}</button>
      </div>
    </div>
  );
};
